import * as Api from '@ViewModels';
import * as React from 'react';
import { useCompaniesQuery } from '../../../../queries';

interface ICompaniesSearchContext {
	filter: Api.ICompaniesSearchFilter;
	setFilter(filter: Api.ICompaniesSearchFilter): void;
	sort: Api.ISortDescriptor;
	setSort(sort: Api.ISortDescriptor): void;
	companies: Api.ICompany[];
	companiesQuery: ReturnType<typeof useCompaniesQuery>;
	selectionState: Api.ESelectionState;
	selectedCompanyIds: string[];
	setSelectionState(state: Api.ESelectionState): void;
	setSelectedCompanyIds(ids: string[]): void;
	excludedCompanyIds: string[];
	setExcludedCompanyIds(ids: string[]): void;
}

interface ICompanyInputContext {
	search: string;
	setSearch(search: string): void;
	onSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

export const getCompanySearchFromCriteria = (criteria: Api.ICompaniesSearchFilter[]) => {
	const selected =
		criteria.find(x => x.property === Api.CompanySearchRequestProperty.All) ??
		criteria.find(x => x.property === Api.CompanySearchRequestProperty.Name) ??
		criteria.find(x => x.property === Api.CompanySearchRequestProperty.Tag);

	return selected.value || '';
};

export const CompaniesSearchInputContext = React.createContext<ICompanyInputContext>(null);
export const useCompaniesInput = () => {
	const context = React.useContext(CompaniesSearchInputContext);
	if (!context) {
		throw new Error('useCompaniesInput can only be used within the <CompaniesContainer /> component');
	}
	return context;
};

export const CompanySearchContext = React.createContext<ICompaniesSearchContext>(null);
export const useCompaniesSearch = () => {
	const context = React.useContext(CompanySearchContext);
	if (!context) {
		throw new Error('useCompaniesSearch can only be used within the <CompaniesContainer /> component');
	}
	return context;
};

export const DefaultCompaniesFilter: Api.ICompaniesSearchFilter = {
	criteria: [{ property: Api.CompanySearchRequestProperty.Name, value: '' }],
};

export const DefaultCompaniesSort: Api.ISortDescriptor = {
	sort: 'asc',
	sortBy: 'companyName',
};

interface ICompaniesTabContext {
	tab: CompaniesTab;
	setTab(tab: CompaniesTab): void;
}

// Tabs
export type CompaniesTab = 'All' | 'Name' | 'Tag';

export const getCompanyCriteriaFromTab = (tab: CompaniesTab) => {
	switch (tab) {
		case 'All':
			return Api.CompanySearchRequestProperty.All;
		case 'Name':
			return Api.CompanySearchRequestProperty.Name;
		case 'Tag':
			return Api.CompanySearchRequestProperty.Tag;
		default:
			break;
	}
};

export const getCompanyTabFromCriteria = (criteria: Api.ICompaniesSearchFilter[]) => {
	const selected =
		criteria.find(x => x.property === Api.CompanySearchRequestProperty.All) ??
		criteria.find(x => x.property === Api.CompanySearchRequestProperty.Name) ??
		criteria.find(x => x.property === Api.CompanySearchRequestProperty.Tag);

	switch (selected.property) {
		case Api.CompanySearchRequestProperty.All:
			return 'All';
		case Api.CompanySearchRequestProperty.Name:
			return 'Name';
		case Api.CompanySearchRequestProperty.Tag:
			return 'Tag';
		default:
			return 'All';
	}
};

export const CompaniesTabContext = React.createContext<ICompaniesTabContext>(null);
export const useCompanyTabs = () => {
	const context = React.useContext(CompaniesTabContext);
	if (!context) {
		throw new Error('useCompanyTabs can only be used within the <CompanyContainer /> component');
	}
	return context;
};
